(async () => {
  const page = document.querySelector('[data-returns-page]');

  if (!page) return;

  const RETURN_REQUEST_MAX_AGE = 2 * 60 * 60 * 1000;
  const customerDataScript = page.querySelector('[data-return-customer-data]');
  const validationMessage = page.querySelector('[data-return-validation-message]');
  const methodStep = page.querySelector('[data-return-step="method"]');
  const itemsStep = page.querySelector('[data-return-step="items"]');
  const summaryStep = page.querySelector('[data-return-step="summary"]');
  const returnItemsEmpty = page.querySelector('[data-return-items-empty]');
  const orderStatus = page.querySelector('[data-return-order-status]');
  const submitUrl = page.dataset.returnSubmitUrl || '';
  const orderRequestUrl = page.dataset.returnOrderRequestUrl || '';
  const simulateLambdaSuccess = false;

  const scrollToPageTop = () => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    });

    window.setTimeout(() => {
      if (window.scrollY > 8) {
        window.scrollTo({ top: 0, behavior: 'auto' });
      }
    }, 500);
  };

  const invalidateReturnRequest = () => {
    try {
      sessionStorage.removeItem('lola_return_request');
      sessionStorage.removeItem('lola_return_order_name');
    } catch (_) {}

    if (validationMessage) validationMessage.hidden = false;
    if (methodStep) methodStep.hidden = true;
    if (itemsStep) itemsStep.hidden = true;
    if (summaryStep) summaryStep.hidden = true;
  };

  let returnRequest;

  try {
    const storedReturnRequest = sessionStorage.getItem('lola_return_request');
    returnRequest = storedReturnRequest ? JSON.parse(storedReturnRequest) : null;
  } catch (_) {
    returnRequest = null;
  }

  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const urlHash = hashParams.get('return');
  const createdAt = Number(returnRequest?.createdAt || 0);
  const expiresAt = Number(returnRequest?.expiresAt || 0);
  const hasValidHash = urlHash && returnRequest?.hash && urlHash === returnRequest.hash;
  const hasValidAge = createdAt && expiresAt && Date.now() - createdAt <= RETURN_REQUEST_MAX_AGE && Date.now() <= expiresAt;

  if (!returnRequest || !hasValidHash || !hasValidAge) {
    invalidateReturnRequest();
    return;
  }

  let customerData;

  try {
    customerData = customerDataScript ? JSON.parse(customerDataScript.textContent) : null;
  } catch (_) {
    customerData = null;
  }

  const normalizedRequestEmail = (returnRequest.email || '').trim().toLowerCase();
  const normalizedCustomerEmail = (customerData?.email || '').trim().toLowerCase();
  const normalizedRequestOrder = (returnRequest.order || '').trim().toLowerCase();
  const customerOrders = Array.isArray(customerData?.orders) ? customerData.orders : [];
  const hasMatchingEmail = normalizedRequestEmail && normalizedRequestEmail === normalizedCustomerEmail;
  const hasMatchingOrder = normalizedRequestOrder && customerOrders.some((order) => String(order).trim().toLowerCase() === normalizedRequestOrder);
  const moneyFormatter = new Intl.NumberFormat(document.documentElement.lang || undefined, {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'EUR',
  });

  const getReasonOptions = () => {
    const reasonOptionsScript = page.querySelector('[data-return-reason-options]');

    try {
      const options = reasonOptionsScript ? JSON.parse(reasonOptionsScript.textContent) : [];
      return Array.isArray(options) ? options : [];
    } catch (_) {
      return [];
    }
  };

  const escapeHtml = (value = '') => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const moneyAmountToCents = (value) => Math.round(Number(value?.amount || 0) * 100);

  const setOrderStatus = (message = '', type = '') => {
    if (!orderStatus) return;

    orderStatus.hidden = !message;
    orderStatus.dataset.status = type;

    orderStatus.textContent = message;
  };

  const getNonReturnableReason = (returnability = {}) => {
    const reason = returnability.reason || '';
    const hasRefundableQuantity = Number(returnability.refundable_quantity || 0) > 0;
    const normalizedReason = reason === 'not_returnable_by_shopify' && hasRefundableQuantity ? 'return_in_process' : reason;
    const reasonKey = String(normalizedReason || '').replace(/(^|[-_])([a-z])/g, (_, __, char) => char.toUpperCase());
    return page.dataset[`returnReason${reasonKey}`]
      || page.dataset.returnReasonNotReturnableByShopify
      || '';
  };

  const getLineItemImageUrl = (lineItem) => {
    const image = lineItem.image || {};
    return image.url || image.src || lineItem.image_url || lineItem.featured_image || '';
  };

  const getSuggestedProductImage = async (lineItem) => {
    const query = lineItem.sku || lineItem.title || lineItem.name || '';

    if (!query) return null;

    try {
      const root = window.Shopify?.routes?.root || '/';
      const url = `${root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=1`;
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      const body = await response.json();
      const product = body?.resources?.results?.products?.[0];
      const image = product?.image || product?.featured_image || product?.featuredImage || null;

      if (typeof image === 'string') return { url: image, alt: product?.title || lineItem.title || lineItem.name || '' };
      if (image?.url) return { url: image.url, alt: image.alt || image.altText || product?.title || lineItem.title || lineItem.name || '' };
      if (image?.src) return { url: image.src, alt: image.alt || product?.title || lineItem.title || lineItem.name || '' };

      return null;
    } catch (_) {
      return null;
    }
  };

  const hydrateMissingLineItemImages = async (lineItems) => Promise.all(lineItems.map(async (lineItem) => {
    if (getLineItemImageUrl(lineItem)) return lineItem;

    const image = await getSuggestedProductImage(lineItem);
    return image ? { ...lineItem, image } : lineItem;
  }));

  const createReasonSelect = (id) => {
    const template = page.querySelector('[data-return-reason-template]');

    if (template?.innerHTML) {
      return `
        <div class="returns-step-items__reason" data-return-reason-title="${escapeHtml(page.querySelector('[data-return-reason-options]') ? getReasonOptions()[0]?.label || '' : '')}">
          ${template.innerHTML.trim().replace(/ReturnReason-Dynamic/g, id)}
        </div>
      `;
    }

    const options = getReasonOptions();
    const label = options[0]?.label || '';
    const optionsHtml = options.map((option) => `<option value="${escapeHtml(option.value)}">${escapeHtml(option.label)}</option>`).join('');

    return `
      <div class="returns-step-items__reason" data-return-reason-title="${escapeHtml(label)}">
        <select id="${escapeHtml(id)}" class="body-s-regular" name="return_reason[]" data-return-item-reason disabled>
          ${optionsHtml}
        </select>
      </div>
    `;
  };

  const getContinueButtonHtml = () => {
    const template = page.querySelector('[data-return-continue-template]');
    return template?.innerHTML?.trim() || '';
  };

  const getSubmitMessageHtml = () => {
    const template = page.querySelector('[data-return-submit-message-template]');
    return template?.innerHTML?.trim() || '';
  };

  const createOrderRequestItem = (lineItem, index) => {
    const itemId = `ReturnItem-Guest-${index}`;
    const reasonId = `ReturnReason-Guest-${index}`;
    const returnability = lineItem.returnability || {};
    const isReturnable = Boolean(returnability.returnable);
    const returnableQuantity = Number(returnability.returnable_quantity || 0);
    const unitPrice = lineItem.discounted_unit_price || lineItem.original_unit_price || null;
    const linePrice = returnability.refundable_amount || lineItem.discounted_total || lineItem.total || null;
    const title = lineItem.title || lineItem.name || '';
    const sku = lineItem.sku || '';
    const image = lineItem.image || {};
    const imageUrl = getLineItemImageUrl(lineItem);
    const imageAlt = image.alt || image.altText || title;
    const nonReturnableReason = getNonReturnableReason(returnability);
    const originalLineItemId = lineItem.original_line_item_id || lineItem.id || '';

    return `
      <li class="returns-step-items__item account-order-detail__item${isReturnable ? '' : ' returns-step-items__item--disabled'}" data-return-item data-return-item-price="${moneyAmountToCents(unitPrice)}" data-return-product-id="${escapeHtml(lineItem.product_id || '')}" data-return-line-item-id="${escapeHtml(originalLineItemId)}" data-return-variant-id="${escapeHtml(lineItem.variant_id || '')}" data-return-sku="${escapeHtml(sku)}" data-return-title="${escapeHtml(title)}" data-return-item-returnable="${isReturnable}">
        <div class="returns-step-items__product account-order-detail__product">
          <span class="account-order-detail__image returns-step-items__image-placeholder">
            ${imageUrl ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(imageAlt)}" class="account-order-detail__image-img" loading="lazy">` : ''}
          </span>

          <div class="account-order-detail__info">
            <div class="returns-step-items__info-head">
              <span class="account-order-detail__title returns-step-items__product-title body-xl-regular" title="${escapeHtml(title)}">${escapeHtml(title)}</span>

              ${isReturnable ? `<label class="returns-step-items__check body-s-regular" for="${escapeHtml(itemId)}">
                <span>${escapeHtml(page.dataset.returnItemSelectLabel || '')}</span>
                <input id="${escapeHtml(itemId)}" type="checkbox" name="return_items[]" value="${escapeHtml(originalLineItemId)}" data-return-item-checkbox>
                <span class="returns-step-items__check-box" aria-hidden="true"></span>
              </label>` : ''}
            </div>

            ${sku ? `<span class="account-order-detail__meta body-s-regular">${escapeHtml(page.dataset.returnRefLabel || '')}. ${escapeHtml(sku)}</span>` : ''}
            <span class="returns-step-items__price body-l-regular" data-return-item-price-output>${escapeHtml(linePrice?.formatted || '')}</span>
            ${isReturnable ? `<div class="returns-step-items__quantity body-s-regular" data-return-quantity-control>
              <button type="button" class="returns-step-items__quantity-button" data-return-quantity-decrease disabled aria-label="-">-</button>
              <input class="returns-step-items__quantity-input body-s-regular" type="number" name="return_quantities[]" min="1" max="${Math.max(returnableQuantity, 1)}" value="${Math.max(returnableQuantity, 1)}" data-return-quantity-input>
              <button type="button" class="returns-step-items__quantity-button" data-return-quantity-increase aria-label="+">+</button>
            </div>` : ''}

            ${isReturnable ? createReasonSelect(reasonId) : ''}
            ${isReturnable ? '' : `<p class="returns-step-items__disabled-reason body-s-regular">${escapeHtml(nonReturnableReason)}</p>`}
          </div>
        </div>
      </li>
    `;
  };

  const requestOrderData = async () => {
    if (!orderRequestUrl) {
      setOrderStatus(page.dataset.returnSubmitConfigError || page.dataset.returnLookupReload || '', 'error');
      return null;
    }

    setOrderStatus(page.dataset.returnLookupLoading || '', 'loading');

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15000);

    try {
      const response = await fetch(orderRequestUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          email: returnRequest.email,
          order_number: returnRequest.order,
        }),
      });
      const body = await response.json().catch(() => ({ error: 'invalid_json_response' }));

      window.clearTimeout(timeout);

      if (!response.ok) {
        setOrderStatus(page.dataset.returnLookupReload || '', 'error');
        return null;
      }

      setOrderStatus('', '');
      return response.ok ? body : null;
    } catch (_) {
      window.clearTimeout(timeout);
      setOrderStatus(page.dataset.returnLookupReload || '', 'error');
      return null;
    }
  };

  const renderOrderRequestItems = async (body) => {
    if (!itemsStep) return Boolean(customerData);

    const order = body?.order;
    const lineItems = Array.isArray(order?.line_items) ? await hydrateMissingLineItemImages(order.line_items) : [];

    if (!order?.name) return false;

    returnRequest.order = order.name;

    if (!lineItems.length) return true;

    const orderItems = document.createElement('div');
    orderItems.className = 'returns-step-items__order';
    orderItems.dataset.returnOrderItems = order.name;
    orderItems.dataset.pickupPrice = page.dataset.pickupPrice || '0';
    orderItems.hidden = true;
    orderItems.innerHTML = `
      <ul class="returns-step-items__list account-order-detail__items" role="list">
        ${lineItems.map((lineItem, index) => createOrderRequestItem(lineItem, index)).join('')}
      </ul>

      <div class="returns-step-items__summary" data-return-summary>
        <div class="returns-step-items__summary-row body-s-regular">
          <span>${escapeHtml(page.dataset.returnSummarySubtotalLabel || '')}</span>
          <span data-return-summary-subtotal>${moneyFormatter.format(0)}</span>
        </div>
        <div class="returns-step-items__summary-row body-s-regular">
          <span>${escapeHtml(page.dataset.returnSummaryPickupLabel || '')}</span>
          <span data-return-summary-pickup>-${moneyFormatter.format(Number(orderItems.dataset.pickupPrice || 0) / 100)}</span>
        </div>
        <div class="returns-step-items__summary-row returns-step-items__summary-row--total body-s-bold">
          <span>${escapeHtml(page.dataset.returnSummaryTotalLabel || '')}</span>
          <span data-return-summary-total>${moneyFormatter.format(0)}</span>
        </div>
        <p class="returns-step-items__tax-note body-s-regular">${escapeHtml(page.dataset.returnTaxNote || '')}</p>
      </div>

      ${getSubmitMessageHtml()}

      <div class="returns-step-items__actions">
        ${getContinueButtonHtml()}
      </div>

      <p class="returns-step-items__note body-s-regular">
        ${escapeHtml(page.dataset.returnItemsStoreNote || '')}
      </p>
    `;

    page.querySelector(`[data-return-order-items="${CSS.escape(order.name)}"]`)?.remove();
    itemsStep.append(orderItems);
    return true;
  };

  if (customerData && (!hasMatchingEmail || !hasMatchingOrder)) {
    if (validationMessage) validationMessage.hidden = false;
    if (methodStep) methodStep.hidden = true;
    if (itemsStep) itemsStep.hidden = true;
    if (summaryStep) summaryStep.hidden = true;
    return;
  }

  const orderRequestBody = await requestOrderData();

  if (orderRequestBody) await renderOrderRequestItems(orderRequestBody);

  if (!customerData) {
    const hasGuestOrderItems = Boolean(orderRequestBody?.order?.name);

    if (!hasGuestOrderItems) {
      if (methodStep) methodStep.hidden = true;
      if (itemsStep) itemsStep.hidden = true;
      if (summaryStep) summaryStep.hidden = true;
      return;
    }
  }

  if (methodStep) methodStep.hidden = false;

  const orderHeading = page.querySelector('[data-return-order-heading]');
  const pageHeading = page.querySelector('[data-return-page-heading]');
  const summaryMeta = page.querySelector('[data-return-summary-meta]');
  const summaryDate = page.querySelector('[data-return-summary-date]');

  if (orderHeading && returnRequest.order) {
    orderHeading.textContent = ` ${page.dataset.returnOrderHeadingLabel || ''} ${returnRequest.order}`;
    orderHeading.hidden = false;
  }

  const emailInput = page.querySelector('input[type="email"], input[name="email"], input[name*="email" i], input[id*="email" i]');
  const orderInput = page.querySelector('input[name="order_name"], input[name*="order" i], input[id*="order" i], input[name*="pedido" i], input[id*="pedido" i]');

  if (emailInput && returnRequest.email && !emailInput.value) {
    emailInput.value = returnRequest.email;
    emailInput.dispatchEvent(new Event('input', { bubbles: true }));
    emailInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  if (orderInput && returnRequest.order && !orderInput.value) {
    orderInput.value = returnRequest.order;
    orderInput.dispatchEvent(new Event('input', { bubbles: true }));
    orderInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  if (returnRequest.order) {
    try {
      sessionStorage.setItem('lola_return_request', JSON.stringify(returnRequest));
    } catch (_) {}
  }

  const enhanceReasonDropdown = (reason) => {
    const dropdownList = reason.querySelector('.dropdown__list');
    if (!dropdownList || dropdownList.querySelector('[data-return-reason-header]')) return;

    const header = document.createElement('div');
    header.className = 'returns-step-items__reason-header';
    header.dataset.returnReasonHeader = '';
    header.innerHTML = `
      <span class="title-6 uppercase">${reason.dataset.returnReasonTitle || ''}</span>
      <button type="button" class="returns-step-items__reason-close" data-return-reason-close aria-label="Cerrar"></button>
    `;

    dropdownList.prepend(header);
  };

  page.querySelectorAll('.returns-step-items__reason').forEach((reason) => {
    reason.addEventListener('click', (event) => {
      if (event.target.closest('[data-return-reason-close]')) {
        event.preventDefault();
        reason.querySelector('details')?.removeAttribute('open');
        reason.querySelector('details')?.classList.remove('is-open');
        return;
      }

      window.setTimeout(() => enhanceReasonDropdown(reason));
    });
  });

  const matchingOrderItems = page.querySelector(`[data-return-order-items="${CSS.escape(returnRequest.order)}"]`);

  const getSummaryValues = (orderItems) => {
    if (!orderItems) return { subtotal: 0, pickupPrice: 0, total: 0 };

    const pickupPrice = Number(orderItems.dataset.pickupPrice || 0);
    const checkedItems = orderItems.querySelectorAll('[data-return-item-checkbox]:checked');
    const subtotal = Array.from(checkedItems).reduce((sum, checkbox) => {
      const item = checkbox.closest('[data-return-item]');
      const quantity = Number(item?.querySelector('[data-return-quantity-input]')?.value || 1);
      return sum + (Number(item?.dataset.returnItemPrice || 0) * quantity);
    }, 0);
    const total = Math.max(subtotal - pickupPrice, 0);

    return { subtotal, pickupPrice, total };
  };

  const updateSummary = (orderItems) => {
    if (!orderItems) return;

    const { subtotal, pickupPrice, total } = getSummaryValues(orderItems);
    const subtotalEl = orderItems.querySelector('[data-return-summary-subtotal]');
    const pickupEl = orderItems.querySelector('[data-return-summary-pickup]');
    const totalEl = orderItems.querySelector('[data-return-summary-total]');

    if (subtotalEl) subtotalEl.textContent = moneyFormatter.format(subtotal / 100);
    if (pickupEl) pickupEl.textContent = `-${moneyFormatter.format(pickupPrice / 100)}`;
    if (totalEl) totalEl.textContent = moneyFormatter.format(total / 100);
  };

  const updateItemPrice = (item) => {
    const priceOutput = item?.querySelector('[data-return-item-price-output]');
    const quantityInput = item?.querySelector('[data-return-quantity-input]');

    if (!priceOutput || !quantityInput) return;

    const quantity = getQuantityState(quantityInput).value || 1;
    const itemPrice = Number(item.dataset.returnItemPrice || 0) * quantity;

    priceOutput.textContent = moneyFormatter.format(itemPrice / 100);
  };

  const renderReturnSummary = (orderItems) => {
    if (!orderItems || !summaryStep) return;

    const summaryItems = summaryStep.querySelector('[data-return-summary-items]');
    const finalSubtotal = summaryStep.querySelector('[data-return-final-subtotal]');
    const finalPickup = summaryStep.querySelector('[data-return-final-pickup]');
    const finalTotal = summaryStep.querySelector('[data-return-final-total]');
    const { subtotal, pickupPrice, total } = getSummaryValues(orderItems);

    if (summaryItems) {
      summaryItems.innerHTML = '';

      const summaryList = document.createElement('ul');
      summaryList.className = 'returns-step-summary__list account-order-detail__items';
      summaryList.setAttribute('role', 'list');

      orderItems.querySelectorAll('[data-return-item-checkbox]:checked').forEach((checkbox) => {
        const item = checkbox.closest('[data-return-item]');
        const quantity = item?.querySelector('[data-return-quantity-input]')?.value || '1';
        const reason = item?.querySelector('[data-return-item-reason]')?.selectedOptions?.[0]?.textContent.trim() || '';
        const summaryItem = item.cloneNode(true);
        const summaryReason = document.createElement('p');
        const reasonLabel = document.createElement('span');
        const reasonValue = document.createElement('span');

        summaryItem.classList.add('returns-step-summary__item');
        summaryItem.querySelector('.returns-step-items__check')?.remove();
        summaryItem.querySelector('[data-return-item-checkbox]')?.remove();

        const quantityInput = summaryItem.querySelector('[data-return-quantity-input]');
        if (quantityInput) quantityInput.value = quantity;
        updateItemPrice(summaryItem);
        summaryItem.querySelectorAll('[data-return-quantity-control] button').forEach((button) => {
          button.remove();
        });
        summaryItem.querySelectorAll('[data-return-quantity-input]').forEach((control) => {
          control.disabled = true;
        });

        summaryReason.className = 'returns-step-summary__reason body-s-regular';
        reasonLabel.className = 'body-s-bold';
        reasonLabel.textContent = `${page.dataset.returnReasonLabel || ''}: `;
        reasonValue.textContent = reason;
        summaryReason.append(reasonLabel, reasonValue);

        const reasonContainer = summaryItem.querySelector('.returns-step-items__reason');
        if (reasonContainer) reasonContainer.replaceWith(summaryReason);

        summaryList.append(summaryItem);
      });

      summaryItems.append(summaryList);
    }

    if (finalSubtotal) finalSubtotal.textContent = moneyFormatter.format(subtotal / 100);
    if (finalPickup) finalPickup.textContent = `-${moneyFormatter.format(pickupPrice / 100)}`;
    if (finalTotal) finalTotal.textContent = moneyFormatter.format(total / 100);
  };

  const getQuantityState = (quantityInput) => {
    if (!quantityInput) return { isValid: false, value: 0, min: 1, max: 1 };

    const min = Number(quantityInput.min || 1);
    const max = Number(quantityInput.max || min);
    const value = Number(quantityInput.value || min);

    return {
      isValid: Number.isFinite(value) && value >= min && value <= max,
      value,
      min,
      max,
    };
  };

  const validateOrderItems = (orderItems) => {
    if (!orderItems) return false;

    const checkedItems = orderItems.querySelectorAll('[data-return-item-checkbox]:checked');
    const hasSelectedItem = checkedItems.length > 0;
    const hasValidQuantities = Array.from(checkedItems).every((checkbox) => {
      const item = checkbox.closest('[data-return-item]');
      const quantityInput = item?.querySelector('[data-return-quantity-input]');
      return getQuantityState(quantityInput).isValid;
    });
    const hasValidReasons = Array.from(checkedItems).every((checkbox) => {
      const item = checkbox.closest('[data-return-item]');
      const reason = item?.querySelector('[data-return-item-reason]');
      return Boolean(reason?.value);
    });

    return hasSelectedItem && hasValidQuantities && hasValidReasons;
  };

  const getSelectedReturnProducts = (orderItems) => {
    if (!orderItems) return [];

    return Array.from(orderItems.querySelectorAll('[data-return-item-checkbox]:checked')).map((checkbox) => {
      const item = checkbox.closest('[data-return-item]');
      const quantityInput = item?.querySelector('[data-return-quantity-input]');

      return {
        product_id: item?.dataset.returnProductId || checkbox.value,
        line_item_id: item?.dataset.returnLineItemId || '',
        variant_id: item?.dataset.returnVariantId || '',
        sku: item?.dataset.returnSku || '',
        title: item?.dataset.returnTitle || '',
        quantity: getQuantityState(quantityInput).value,
        reason: item?.querySelector('[data-return-item-reason]')?.value || '',
      };
    });
  };

  const updateSubmitMessage = (orderItems, message = '', type = '') => {
    const messageEl = orderItems?.querySelector('[data-return-submit-message]');

    if (!messageEl) return;

    const messageText = messageEl.querySelector('[data-return-submit-message-text]') || messageEl;
    messageText.textContent = message;
    messageEl.hidden = !message;
    messageEl.dataset.status = type;
  };

  let summaryNoticeTimeout;

  const showSummaryNotice = () => {
    const notice = summaryStep?.querySelector('[data-return-summary-notice]');

    if (!notice) return;

    if (summaryNoticeTimeout) clearTimeout(summaryNoticeTimeout);

    notice.hidden = false;
    window.requestAnimationFrame(() => {
      notice.classList.add('is-visible');
    });

    summaryNoticeTimeout = window.setTimeout(() => {
      notice.classList.remove('is-visible');
      window.setTimeout(() => {
        notice.hidden = true;
      }, 300);
    }, 8000);
  };

  const updateContinueButton = (orderItems) => {
    if (!orderItems) return;

    const continueButton = orderItems.querySelector('[data-return-items-continue]');
    const isValid = validateOrderItems(orderItems);

    if (continueButton) {
      continueButton.setAttribute('aria-disabled', String(!isValid));
    }

    if (isValid) {
      updateSubmitMessage(orderItems, '', '');
    }
  };

  const setContinueButtonLoading = (continueButton, isLoading) => {
    if (!continueButton) return;

    const label = continueButton.querySelector('.lola-btn__label');
    if (label) {
      label.textContent = isLoading
        ? page.dataset.returnItemsSubmittingLabel || 'Processing return...'
        : page.dataset.returnItemsContinueLabel || 'Continue';
    }

    continueButton.classList.toggle('is-saving', isLoading);
    continueButton.disabled = isLoading;
    continueButton.setAttribute('aria-disabled', String(isLoading));
  };

  const updateOrderItemsState = (orderItems) => {
    orderItems?.querySelectorAll('[data-return-item]').forEach((item) => updateItemPrice(item));
    updateSummary(orderItems);
    updateContinueButton(orderItems);
  };

  page.querySelectorAll('[data-return-order-items]').forEach((orderItems) => {
    orderItems.hidden = true;
  });

  if (matchingOrderItems) {
    matchingOrderItems.hidden = false;
    updateOrderItemsState(matchingOrderItems);
    if (returnItemsEmpty) returnItemsEmpty.hidden = true;
  } else if (returnItemsEmpty) {
    returnItemsEmpty.hidden = false;
  }

  page.querySelectorAll('[data-return-method="pickup"]').forEach((trigger) => {
    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      if (methodStep) methodStep.hidden = true;
      if (itemsStep) itemsStep.hidden = false;
      scrollToPageTop();
    });
  });

  page.querySelectorAll('[data-return-item-checkbox]').forEach((checkbox) => {
    checkbox.checked = false;

    const initialItem = checkbox.closest('.returns-step-items__item');
    const initialReason = initialItem?.querySelector('[data-return-item-reason]');

    if (initialReason) {
      initialReason.disabled = true;
      initialReason.required = false;
      initialReason.value = '';
      initialReason.selectedIndex = 0;
      const reasonDropdown = initialReason.closest('dropdown-input');
      const reasonLabel = reasonDropdown?.querySelector('[data-label]');

      if (reasonLabel && initialReason.options[0]) {
        reasonLabel.textContent = initialReason.options[0].label;
      }
    }

    checkbox.addEventListener('change', () => {
      const item = checkbox.closest('.returns-step-items__item');
      const reason = item?.querySelector('[data-return-item-reason]');

      if (reason) {
        reason.disabled = !checkbox.checked;
        reason.required = checkbox.checked;
        if (!checkbox.checked) {
          reason.value = '';
          reason.selectedIndex = 0;
          const reasonDropdown = reason.closest('dropdown-input');
          const reasonLabel = reasonDropdown?.querySelector('[data-label]');

          if (reasonLabel && reason.options[0]) {
            reasonLabel.textContent = reason.options[0].label;
          }
        }
      }

      updateOrderItemsState(checkbox.closest('[data-return-order-items]'));
    });
  });

  page.querySelectorAll('[data-return-quantity-control]').forEach((quantityControl) => {
    const quantityInput = quantityControl.querySelector('[data-return-quantity-input]');
    const decreaseButton = quantityControl.querySelector('[data-return-quantity-decrease]');
    const increaseButton = quantityControl.querySelector('[data-return-quantity-increase]');

    const clampQuantity = () => {
      const min = Number(quantityInput.min || 1);
      const max = Number(quantityInput.max || min);
      const currentValue = Number(quantityInput.value || min);
      quantityInput.value = Math.min(Math.max(currentValue, min), max);
      if (decreaseButton) decreaseButton.disabled = Number(quantityInput.value) <= min;
      if (increaseButton) increaseButton.disabled = Number(quantityInput.value) >= max;
    };

    quantityInput.value = quantityInput.max || quantityInput.value || quantityInput.min || '1';
    clampQuantity();

    decreaseButton?.addEventListener('click', () => {
      quantityInput.value = Number(quantityInput.value || 1) - 1;
      clampQuantity();
      updateOrderItemsState(quantityInput.closest('[data-return-order-items]'));
    });

    increaseButton?.addEventListener('click', () => {
      quantityInput.value = Number(quantityInput.value || 1) + 1;
      clampQuantity();
      updateOrderItemsState(quantityInput.closest('[data-return-order-items]'));
    });

    quantityInput.addEventListener('change', () => {
      clampQuantity();
      updateOrderItemsState(quantityInput.closest('[data-return-order-items]'));
    });
    quantityInput.addEventListener('input', () => {
      clampQuantity();
      updateOrderItemsState(quantityInput.closest('[data-return-order-items]'));
    });
  });

  page.querySelectorAll('[data-return-item-reason]').forEach((reason) => {
    reason.addEventListener('change', () => {
      updateOrderItemsState(reason.closest('[data-return-order-items]'));
    });
  });

  page.querySelectorAll('[data-return-items-continue]').forEach((continueButton) => {
    continueButton.addEventListener('click', async (event) => {
      const orderItems = continueButton.closest('[data-return-order-items]');

      if (!validateOrderItems(orderItems)) {
        event.preventDefault();
        updateContinueButton(orderItems);
        updateSubmitMessage(orderItems, page.dataset.returnItemsValidationMessage || '', 'error');
        return;
      }

      event.preventDefault();

      if (!submitUrl && !simulateLambdaSuccess) {
        updateSubmitMessage(orderItems, page.dataset.returnSubmitConfigError || '', 'error');
        return;
      }

      const payload = {
        email: returnRequest.email,
        order_number: returnRequest.order,
        products: getSelectedReturnProducts(orderItems),
      };

      setContinueButtonLoading(continueButton, true);
      updateSubmitMessage(orderItems, '', '');

      try {
        const response = simulateLambdaSuccess
          ? { ok: true }
          : await fetch(submitUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          });

        if (!response.ok) throw new Error('Return request failed');

        renderReturnSummary(orderItems);
        if (itemsStep) itemsStep.hidden = true;
        if (summaryStep) summaryStep.hidden = false;
        if (pageHeading) pageHeading.textContent = `${page.dataset.returnSummaryHeading || ''} ${returnRequest.order}`.trim();
        if (summaryMeta) summaryMeta.hidden = false;
        if (summaryDate) {
          const today = new Date();
          const formattedDate = new Intl.DateTimeFormat('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }).format(today);

          summaryDate.textContent = `${page.dataset.returnSummaryDateLabel || ''} ${formattedDate}`.trim();
        }
        scrollToPageTop();
        showSummaryNotice();
      } catch (_) {
        setContinueButtonLoading(continueButton, false);
        updateContinueButton(orderItems);
        updateSubmitMessage(orderItems, page.dataset.returnSubmitError || '', 'error');
      }
    });
  });

  updateOrderItemsState(matchingOrderItems);
})();